import React, { useEffect, useMemo, useState } from 'react';
import './ActivityMapSection.css';
import FeedMap from '../../dashboard/feed/FeedMap';
import { listPublishedRequests } from '../../../services/requestsApi';
import { listPublishedPublications } from '../../../services/publicationsApi';
import {
  PROVIDER_CATEGORIES,
  buildPublicationFeedItem,
  buildRequestFeedItem,
} from '../../../utils/feedItems';
import useActivityMarkers from '../../../hooks/useActivityMarkers';

const REQUEST_STATUS_ALLOWLIST = new Set([
  'published',
  'completed',
  'completed_ok',
  'completed_error',
]);

export default function ActivityMapSection() {
  const [requestItems, setRequestItems] = useState([]);
  const [publicationItems, setPublicationItems] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadData = async () => {
      setLoadingFeed(true);
      setError('');
      try {
        const [requests, publications] = await Promise.all([
          listPublishedRequests({ limit: 12 }),
          listPublishedPublications({ limit: 18 }),
        ]);
        if (ignore) {
          return;
        }

        const preparedRequests = requests
          .filter(item => REQUEST_STATUS_ALLOWLIST.has(item.status || ''))
          .map(buildRequestFeedItem)
          .slice(0, 6);

        const preparedPublications = publications
          .filter(
            item =>
              item.status === 'published' &&
              (!item.companyCategory || PROVIDER_CATEGORIES.has(item.companyCategory))
          )
          .map(buildPublicationFeedItem)
          .slice(0, 9);

        setRequestItems(preparedRequests);
        setPublicationItems(preparedPublications);
      } catch (err) {
        if (!ignore) {
          console.error('No se pudo cargar la actividad del mapa de inicio', err);
          setError('No pudimos cargar el mapa de actividad. Intentalo mas tarde.');
          setRequestItems([]);
          setPublicationItems([]);
        }
      } finally {
        if (!ignore) {
          setLoadingFeed(false);
        }
      }
    };

    loadData();

    return () => {
      ignore = true;
    };
  }, []);

  const pickupOnlyRequests = useMemo(
    () =>
      requestItems.map(item => ({
        ...item,
        dropoffLocation: '',
      })),
    [requestItems]
  );

  const { markers, loading: loadingMarkers } = useActivityMarkers(
    pickupOnlyRequests,
    publicationItems
  );

  const loading = loadingFeed || loadingMarkers;

  return (
    <section className="live-network" aria-labelledby="live-network-title">
      <div className="live-network__inner">
        <div className="live-network__content">
          <p className="live-network__eyebrow">Live Network</p>
          <h2 id="live-network-title">Clientes y proveedores conectando ahora</h2>
          <p className="live-network__description">
            Tu red ya está en movimiento: clientes publican cargas urgentes mientras proveedores libres
            levantan la mano para cubrirlas. Coordina a tu equipo con visibilidad en vivo y sincronicen
            la próxima conexión en minutos.
          </p>
          <div className="live-network__stats">
            <div className="live-network__stat">
              <span className="live-network__stat-value">120+</span>
              <span className="live-network__stat-label">Clientes en línea</span>
            </div>
            <div className="live-network__stat">
              <span className="live-network__stat-value">350+</span>
              <span className="live-network__stat-label">Proveedores disponibles</span>
            </div>
          </div>
          {error ? <p className="live-network__error">{error}</p> : null}
        </div>

        <div className="live-network__visual" aria-live="polite">
          <div className="live-network__map-frame">
            <div className="live-network__map-browser" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div className="live-network__map-content">
              <FeedMap markers={markers} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
